import { styled } from 'design-system';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { Journal } from '../../../resources/journal';
import { SubmissionReadyChecklistV1 } from './submission-ready-checklist-v1';

type CheckItem = {
  critical: boolean;
  checkKey: string;
  label: string;
  content: {
    text: string;
    links: {
      text: string;
      url: string;
    }[];
  };
  // Other properties
};

interface ChecksData {
  headerImage: string;
  headerTitle: string;
  level: number;
  data: Record<string, CheckItem[]>;
}

export const ChecklistPreview = ({ journal }: { journal: Journal }) => {
  const { t } = useTranslation(['common']);
  const [languageGroupedData, setLanguageGroupedData] = useState<ChecksData>({
    headerImage: '',
    headerTitle: '',
    level: 0,
    data: {},
  });
  const [technicalGroupedData, setTechnicalGroupedData] = useState<ChecksData>({
    headerImage: '',
    headerTitle: '',
    level: 0,
    data: {},
  });

  useEffect(() => {
    if (journal.checklist.checks) {
      const langChecks = Object.values(journal.checklist.checks).filter(
        (item) => item.grouping === 'language',
      );
      const techChecks = Object.values(journal.checklist.checks).filter(
        (item) => item.grouping !== 'language',
      );

      const langChecksData = langChecks.reduce<Record<string, CheckItem[]>>(
        (result, obj) => {
          const { groupingLabel, ...rest } = obj;

          result[groupingLabel] = result[groupingLabel] || [];
          result[groupingLabel]?.push(rest) ?? (result[groupingLabel] = [rest]);

          return result;
        },
        {},
      );

      const techChecksData = techChecks.reduce<Record<string, CheckItem[]>>(
        (result, obj) => {
          const { groupingLabel, ...rest } = obj;

          result[groupingLabel] = result[groupingLabel] || [];
          result[groupingLabel]?.push(rest) ?? (result[groupingLabel] = [rest]);
          return result;
        },
        {},
      );

      setLanguageGroupedData({
        headerImage: '/language-quality.svg',
        headerTitle: t('language_quality', 'Language Quality'),
        level: 1,
        data: langChecksData,
      });
      setTechnicalGroupedData({
        headerImage: '/technical-checks.svg',
        headerTitle: t('technical_checks', 'Technical Checks'),
        level: 2,
        data: techChecksData,
      });
    }
  }, [journal.checklist.checks, t]);

  return (
    <>
      <CheckListGroup aria-label="Checklist preview">
        {languageGroupedData &&
          Object.keys(languageGroupedData.data).length > 0 && (
            <SubmissionReadyChecklistV1
              headerImage={languageGroupedData.headerImage}
              headerTitle={languageGroupedData.headerTitle}
              level={languageGroupedData.level}
              data={languageGroupedData.data}
            />
          )}
        {technicalGroupedData &&
          Object.keys(technicalGroupedData.data).length > 0 && (
            <SubmissionReadyChecklistV1
              headerImage={technicalGroupedData.headerImage}
              headerTitle={technicalGroupedData.headerTitle}
              level={technicalGroupedData.level}
              data={technicalGroupedData.data}
            />
          )}
      </CheckListGroup>
    </>
  );
};

const CheckListGroup = styled('div', {
  display: 'flex',
  gap: 30,
  justifyContent: 'center',
});
